import { IconDefinition } from "../types";
import * as Config from '../config';
import { PlatformCapabilitiesModulesPortTypes } from "../status"
import Locale from '../utils/Locale';
import { PlatformCapabilities, PlatformCapabilitiesModules, PlatformCapabilitiesControllers, PlatformCapabilitiesPorts } from "../status";

export const PortTypeNames = {
    'Unknown': Locale.gettext('Unknown'),
    'HighVoltage': Locale.gettext('Relay'),
    'LowVoltage': Locale.gettext('Heater'),
    'Serial': Locale.gettext('Bus'),
    'Switch': Locale.gettext('Flow Switch'),
    'Thermistor': Locale.gettext('Temp Sensor'),
    'Actuator': Locale.gettext('Valve'),
}

/*
{
    Controllers: {
        ControllerUUID: "dummy-classic-uuid-1234",
        Modules: [ {
            PortTypes: {
                Bus: [ {
                    BusClasses: [ {
                        BusClassName: "GoldlineBus",
                        ConfiguredDevices: [ {
                            UUID: "9e6902fc-7f06-4f1d-92eb-12aSALTCELL1",
                            Addresses: [
                                { Address: "1" }
                            ],
                            AddressUniqueType: null,
                            Invalid: true,
                            InvalidReason: "E.g: device address is not unique"
                        } ],
                        Invalid: true,
                        InvalidReason: "E.g: device bus class is not cohabitatable"
                    } ]
                } ],
                LowVoltage: [ {
                    "PortNumber": 1,
                    "ConfiguredDevices": [ {
                        "UUID": "d17f728d-0a3e-41b6-9121-123f70HEATER"
                    } ]
                }, {
                    "PortNumber": 2,
                    "ConfiguredDevices": [ {
                        "UUID": "1fb7f3f4-b331-4ff1-8dd1-FILTER1RETUR"
                    } ]
                }, {
                    "PortNumber": 3
                } ],
                Bluetooth: [ {
                    "ConfiguredDevices": [ {
                        "UUID": "1e9c954f-796f-421f-830f-285971a091d7",
                        "Addresses": [
                            "04:EE:03:DB:5E:94"
                        ]
                    } ]
                } ]
            }
        } ]
    }
}
*/
export type PortUsage = Omit<PlatformCapabilities, "Controllers"> & {
    Controllers: PortUsageControllers[],
    /** True on any error. E.g if any of the Controllers, any of the Modules, any of the PortTypes has invalid values. */
    Invalid: boolean,
}
export type PortUsageControllers = Omit<PlatformCapabilitiesControllers, "Modules"> & {
    Modules: PortuUsageModules[]
}
export type PortUsagePortsBase = PlatformCapabilitiesPorts & {
    ConfiguredDevices?: PortUsageConfiguredDevice[],
    Invalid?: boolean,
    InvalidReason?: string,
    InvalidAddresses?: { Address: string }[],
};
export type PortUsagePortsBus = PortUsagePortsBase & {
    BusClasses?: PortUsageBusClasses[],
};
export type PortUsagePortsRelay = PortUsagePortsBase & {
    BookedAs: PortUsagePortBookingType[],
};

/** Purpose of the booking
 * The easiest way to understand the concept is to imagine a Pump that is controlled by commands via the serial bus
 * or imagine a sensor that uses Bluetooth as primary communication interface,
 * but connected to the mains through a relay, so by the relay the device can be completely turned off.
 * Multiple devices can be turned off by a single power port relay so power port can be shared.
 * If the device
 */
export enum PortUsagePortBookingType {
    /** Controlling the device. Not shareable. */
    ControlPort = "controlport",
    /** Power Port of the device Sharable */
    PowerPort = "powerport",
}

export type PortUsageInvalidAddressesByAddressUiqueType = {
    Addresses: string[],
    nonfixable: boolean,
}
export type PortUsageBusClasses = {
    BusClassName: string,
    ConfiguredDevices: PortUsageConfiguredDevice[],
    Invalid?: boolean,
    InvalidReason?: string,
    InvalidAddressesByAddressUiqueType?: { [ key: string ]: PortUsageInvalidAddressesByAddressUiqueType },
}
export type PortUsageConfiguredDeviceAddress = {
    Address: string,
}
export type PortUsageConfiguredDevice = {
    UUID: string,
    Addresses?: PortUsageConfiguredDeviceAddress[],
    AddressUniqueType?: string | null,
    Invalid?: boolean,
    InvalidReason?: string
}
export type PortuUsageModules = Omit<PlatformCapabilitiesModules, "PortTypes"> & {
    PortTypes: {
        [ PlatformCapabilitiesModulesPortTypes.Actuator ]?: PortUsagePortsBase[],
        [ PlatformCapabilitiesModulesPortTypes.Bus ]?: PortUsagePortsBus[],
        [ PlatformCapabilitiesModulesPortTypes.Bluetooth ]?: PortUsagePortsBase[],
        [ PlatformCapabilitiesModulesPortTypes.ChemistryORP ]?: PortUsagePortsBase[],
        [ PlatformCapabilitiesModulesPortTypes.ChemistryPH ]?: PortUsagePortsBase[],
        [ PlatformCapabilitiesModulesPortTypes.FlowSwitch ]?: PortUsagePortsBase[],
        [ PlatformCapabilitiesModulesPortTypes.HighVoltage ]?: PortUsagePortsRelay[],
        [ PlatformCapabilitiesModulesPortTypes.InterfaceButtons ]?: PortUsagePortsBase[],
        [ PlatformCapabilitiesModulesPortTypes.LightDriverLowVoltage ]?: PortUsagePortsBase[],
        [ PlatformCapabilitiesModulesPortTypes.LowVoltage ]?: PortUsagePortsBase[],
        [ PlatformCapabilitiesModulesPortTypes.Ma ]?: PortUsagePortsBase[],
        [ PlatformCapabilitiesModulesPortTypes.Thermistor ]?: PortUsagePortsBase[],
    },
}


